
export function formatMoney(n) {
  try {
    return new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: "MXN",
    }).format(Number(n || 0));
  } catch {
    return `$ ${Number(n || 0).toFixed(2)}`;
  }
}

export function segColors(n) {
  const out = [];
  for (let i = 0; i < n; i++) {
    const h = (i * (360 / Math.max(1, n))) % 360;
    out.push(`hsl(${h} 80% 60%)`);
  }
  return out;
}

export function donutStyle(values) {
  const v = values.map((x) => Math.max(0, Number(x || 0)));
  const sum = v.reduce((s, x) => s + x, 0) || 1;
  const colors = segColors(v.length);
  let acc = 0;
  const stops = v
    .map((x, i) => {
      const start = (acc / sum) * 100;
      acc += x;
      const end = (acc / sum) * 100;
      return `${colors[i]} ${start}% ${end}%`;
    })
    .join(", ");
  const bg = `conic-gradient(${stops})`;
  return { background: bg };
}

export function daySeq(startDate, endDate) {
  const out = [];
  const d0 = new Date(startDate);
  d0.setHours(0, 0, 0, 0);
  const d1 = new Date(endDate);
  d1.setHours(0, 0, 0, 0);
  for (let d = d0; d <= d1; d = new Date(d.getTime() + 86400000))
    out.push(new Date(d));
  return out;
}

export function monthKey(d) {
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  return `${y}-${String(m).padStart(2, "0")}`;
}

export function monthShort(d) {
  return [
    "Ene",
    "Feb",
    "Mar",
    "Abr",
    "May",
    "Jun",
    "Jul",
    "Ago",
    "Sep",
    "Oct",
    "Nov",
    "Dic",
  ][d.getMonth()];
}

export function escapeHtml(str) {
  return String(str || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export function formatMarkdownToHtml(md) {
  const lines = escapeHtml(md).split(/\r?\n/);
  let html = "";
  let inList = false;
  let inOList = false;
  for (const raw of lines) {
    const line = raw;
    if (/^\s*-\s+/.test(line)) {
      if (!inList) {
        html += "<ul>";
        inList = true;
      }
      const item = line
        .replace(/^\s*-\s+/, "")
        .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
      html += `<li>${item}</li>`;
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      if (inList) {
        html += "</ul>";
        inList = false;
      }
      if (!inOList) {
        html += "<ol>";
        inOList = true;
      }
      const item = line
        .replace(/^\s*\d+\.\s+/, "")
        .replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
      html += `<li>${item}</li>`;
      continue;
    }
    if (inList) {
      html += "</ul>";
      inList = false;
    }
    if (inOList) {
      html += "</ol>";
      inOList = false;
    }
    const text = line.replace(/\*\*(.*?)\*\*/g, "<b>$1</b>");
    if (text.trim()) html += `<p>${text}</p>`;
  }
  if (inList) html += "</ul>";
  if (inOList) html += "</ol>";
  return html;
}

export function normalizeText(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export const numberWords = {
  un: 1,
  uno: 1,
  una: 1,
  dos: 2,
  tres: 3,
  cuatro: 4,
  cinco: 5,
  seis: 6,
  siete: 7,
  ocho: 8,
  nueve: 9,
  diez: 10,
  doce: 12,
  docena: 12,
};

export function extractQuantity(text) {
  const lower = normalizeText(text);
  if (/\bmedia\s+docena\b/.test(lower)) return 6;
  if (/\bdocena\b/.test(lower)) return 12;
  if (/\bun\s+par\b/.test(lower) || /\bpar\b/.test(lower)) return 2;
  const m = String(text || "").match(/\b(\d+)\b/);
  if (m) return Number(m[1]);
  const words = lower.split(" ");
  for (const w of words) {
    if (numberWords[w] !== undefined) return numberWords[w];
  }
  return 1;
}

export function toSingular(w) {
  if (!w) return w;
  if (w.endsWith("ces") && w.length > 4) return w.slice(0, -3) + "z";
  if (w.endsWith("es") && w.length > 4) return w.slice(0, -2);
  if (w.endsWith("s") && w.length > 3) return w.slice(0, -1);
  return w;
}

export function tokenize(s) {
  const stop = new Set([
    "me",
    "por",
    "favor",
    "porfa",
    "de",
    "del",
    "la",
    "el",
    "los",
    "las",
    "al",
    "a",
    "un",
    "una",
    "uno",
    "unos",
    "unas",
    "dos",
    "tres",
    "orden",
    "ordenes",
    "ord",
    "quiero",
    "agrega",
    "añade",
    "pon",
    "suma",
    "compra",
    "compras",
    "pedido",
    "pedidos",
    "carrito",
    "carro",
    "cesta",
    "canasta",
    "basket",
    "otro",
    "otra",
    "otros",
    "mas",
    "más",
  ]);
  const arr = normalizeText(s)
    .split(" ")
    .filter(Boolean)
    .map(toSingular)
    .filter((w) => !stop.has(w))
    .filter((w) => isNaN(Number(w)))
    .filter((w) => w.length >= 2);
  return Array.from(new Set(arr));
}

export function productNameFromPart(p) {
  const toks = tokenize(p);
  return toks.join(" ").trim();
}

export function finalPrice(unit, discount) {
  const u = Number(unit || 0);
  const d = Number(discount || 0);
  return d > 0 ? Number((u * (1 - d / 100)).toFixed(2)) : u;
}

export function rangeFor(p) {
  const end = new Date();
  const start = new Date(end);
  if (p === "hoy") start.setHours(0, 0, 0, 0);
  else if (p === "7d") start.setDate(end.getDate() - 6);
  else if (p === "30d") start.setDate(end.getDate() - 29);
  else if (p === "mes") {
    start.setDate(1);
  }
  return {
    start: start.toISOString(),
    end: end.toISOString(),
    startDate: start,
    endDate: end,
  };
}

export function previousRange(cur) {
  const start = new Date(cur.startDate);
  const end = new Date(cur.endDate);
  const isTodayRange =
    start.getHours() === 0 && start.getMinutes() === 0 && start.getSeconds() === 0 &&
    start.toDateString() === new Date().toDateString();
  if (isTodayRange) {
    const y = new Date(start.getFullYear(), start.getMonth(), start.getDate() - 1);
    const prevStart = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 0, 0, 0, 0);
    const prevEnd = new Date(y.getFullYear(), y.getMonth(), y.getDate(), 23, 59, 59, 999);
    return { start: prevStart.toISOString(), end: prevEnd.toISOString() };
  }
  const delta = end.getTime() - start.getTime();
  const prevEnd = new Date(start.getTime() - 1);
  const prevStart = new Date(prevEnd.getTime() - delta);
  return { start: prevStart.toISOString(), end: prevEnd.toISOString() };
}
